import { useSearchParams } from "react-router-dom";
import { DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTheme } from "next-themes";
import {
  Heart,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Wheat,
  Beef,
  CandyOff,
  Droplets,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useBiteBalanceTracking } from "../../hooks/useApi";

const CATEGORY_COLORS = [
  "hsl(142, 76%, 36%)",
  "hsl(var(--primary))",
  "hsl(45, 93%, 47%)",
  "hsl(var(--destructive))",
];

const DIST_COLORS = [
  "hsl(142, 76%, 36%)",
  "hsl(var(--primary))",
  "hsl(45, 93%, 47%)",
  "hsl(var(--destructive))",
];

const RATIO_ICONS: Record<string, React.ElementType> = {
  carb_fiber: Wheat,
  protein_fiber: Beef,
  sugar_load: CandyOff,
  fat_quality: Droplets,
};

const bandColor = (band: string | null | undefined) => {
  if (band == null || band === "") return "text-muted-foreground";
  const b = band.toLowerCase();
  if (b === "excellent") return "text-emerald-600";
  if (b === "good") return "text-primary";
  if (b.includes("moderate") || b.includes("improvement")) return "text-amber-500";
  return "text-destructive";
};

const normalizeBand = (label: string | null | undefined) => {
  if (label == null || label === "") return "poor";
  const l = label.toLowerCase();
  if (l === "excellent") return "excellent";
  if (l === "good") return "good";
  if (l.includes("moderate") || l.includes("improvement")) return "moderate";
  return "poor";
};

const AdminBiteBalance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const urlDate = searchParams.get("date");
  const selectedDate = urlDate ? dayjs(urlDate) : dayjs();

  const handleDateChange = (date: Dayjs | null) => {
    setSearchParams({ date: (date ?? dayjs()).format("YYYY-MM-DD") });
  };

  const dateParam = selectedDate.format("YYYY-MM-DD");
  const { data, isLoading } = useBiteBalanceTracking(dateParam);

  const tooltipStyle = {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    color: "hsl(var(--foreground))",
  };

  // --- Summary stat cards ---
  const summary = data?.summary;
  const distribution = data?.distribution ?? [];

  const distMap = Object.fromEntries(distribution.map(d => [d.id, d]));

  const statCards = [
    {
      title: "Avg BBS",
      value: summary?.avg_bbs != null ? String(summary.avg_bbs) : "—",
      description: summary?.band?.label ?? "",
      icon: Heart,
    },
    {
      title: "Excellent (80-100)",
      value: distMap["excellent"]?.count != null ? String(distMap["excellent"].count) : "—",
      description: distMap["excellent"] ? `${distMap["excellent"].pct_of_users}% of users` : "",
      icon: CheckCircle,
    },
    {
      title: "Good (60-79)",
      value: distMap["good"]?.count != null ? String(distMap["good"].count) : "—",
      description: distMap["good"] ? `${distMap["good"].pct_of_users}% of users` : "",
      icon: TrendingUp,
    },
    {
      title: "Caution (40-59)",
      value: distMap["caution"]?.count != null ? String(distMap["caution"].count) : "—",
      description: distMap["caution"] ? `${distMap["caution"].pct_of_users}% of users` : "",
      icon: TrendingDown,
    },
    {
      title: "Poor (0-39)",
      value: distMap["poor"]?.count != null ? String(distMap["poor"].count) : "—",
      description: distMap["poor"] ? `${distMap["poor"].pct_of_users}% of users` : "",
      icon: AlertTriangle,
    },
  ];

  // --- Health ratio cards ---
  const healthRatios = data?.health_ratios ?? [];

  // --- Health insight distribution (stacked bar) ---
  const rawHealthInsightDist = data?.health_insight_distribution ?? [];

  const healthInsightDist = rawHealthInsightDist.map(item => {
    const row: Record<string, string | number> = { metric: item.title };
    item.bands.forEach(b => {
      const key = normalizeBand(b.label);
      row[key] = (Number(row[key] ?? 0) + b.count);
      row[`${key}_pct`] = parseFloat(((Number(row[`${key}_pct`] ?? 0)) + b.pct).toFixed(1));
    });
    return row;
  });

  // --- BBS distribution bar ---
  const bbsDistribution = data?.bbs_distribution ?? [];

  // --- Category split pie ---
  const bbsByCategory = distribution.map(d => ({
    name: d.label.split(" ")[0],
    value: d.pct_category_split,
    count: d.count,
  }));

  // --- Monthly trend ---
  const bbsTrend = (data?.bbs_monthly_trend ?? [])
    .map(t => ({
      month: `${t.label} '${String(t.year).slice(2)}`,
      avg: t.average,
      median: t.median,
    }));

  // --- Demographic bar ---
  const demographic = data?.bbs_by_demographic;
  const bbsByDemographic = [
    ...(demographic?.gender ?? []),
    ...(demographic?.age_group ?? []),
  ]
    .filter(d => d.average_bbs !== null)
    .map(d => ({ group: d.label, avg: d.average_bbs }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Bite Balance Score Tracking
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Per-user BBS monitoring with health insights, trends and component breakdown
          </p>
        </div>
        <ConfigProvider
          theme={{
            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            token: {
              colorPrimary: isDark ? "hsl(221, 83%, 53%)" : "hsl(221, 65%, 58%)",
              colorBgContainer: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
              colorBgElevated: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
              colorText: isDark ? "hsl(210, 40%, 98%)" : "hsl(240, 10%, 10%)",
              colorTextPlaceholder: isDark ? "hsl(215, 20%, 65%)" : "hsl(240, 4%, 46%)",
              colorBorder: isDark ? "hsl(224, 71%, 15%)" : "hsl(240, 6%, 90%)",
              borderRadius: 8,
            },
          }}
        >
          <DatePicker
            format="MMM D, YYYY"
            value={selectedDate}
            onChange={handleDateChange}
            allowClear={false}
          />
        </ConfigProvider>
      </div>

      {/* BBS Summary Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {statCards.map(stat => (
          <Card key={stat.title}>
            <CardContent className="p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <stat.icon className="h-4 w-4 text-primary" />
                </div>
                <p className="text-xs font-medium text-muted-foreground">{stat.title}</p>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {isLoading ? "..." : stat.value}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Health Ratio Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-16 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))
          : healthRatios.map(ratio => {
            const Icon = RATIO_ICONS[ratio.id] ?? Wheat;
            return (
              <Card key={ratio.id}>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">{ratio.label}</p>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-bold text-foreground">{ratio.value}</p>
                    <Badge
                      variant="secondary"
                      className={`${bandColor(ratio.label_band)} border-0 bg-transparent px-0 text-xs font-semibold`}
                    >
                      {ratio.label_band ?? "—"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>

      {/* Health Insight Status Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Health Insight Status Distribution</CardTitle>
          <CardDescription>User count by status range per metric</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-56 items-center justify-center text-muted-foreground">Loading...</div>
          ) : healthInsightDist.length === 0 ? (
            <div className="flex h-56 items-center justify-center text-muted-foreground">No data available</div>
          ) : (
            <>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={healthInsightDist}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="metric" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string) => {
                        return [`${value} users`, name.charAt(0).toUpperCase() + name.slice(1)];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="excellent" name="Excellent" fill={DIST_COLORS[0]} stackId="a" />
                    <Bar dataKey="good" name="Good" fill={DIST_COLORS[1]} stackId="a" />
                    <Bar dataKey="moderate" name="Moderate/Needs Improvement" fill={DIST_COLORS[2]} stackId="a" />
                    <Bar dataKey="poor" name="Poor" fill={DIST_COLORS[3]} stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Per-metric percentage breakdown */}
              <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                {rawHealthInsightDist.map((item, i) => (
                  <div key={item.id} className="space-y-1">
                    <p className="text-xs font-semibold text-foreground">{item.title}</p>
                    {item.bands.map((band, j) => (
                      <div key={j} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: DIST_COLORS[j % DIST_COLORS.length] }}
                          />
                          <span className="text-muted-foreground">{band.label}</span>
                        </div>
                        <span className="font-semibold text-foreground">{band.pct}%</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* BBS Distribution + Category Split */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">BBS Distribution</CardTitle>
            <CardDescription>Score distribution across all users</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">Loading...</div>
            ) : bbsDistribution.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">No data available</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bbsDistribution}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="range" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value} users`, "Count"]} />
                    <Bar dataKey="users" name="Users" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Category Split</CardTitle>
            <CardDescription>Users by BBS category</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">Loading...</div>
            ) : bbsByCategory.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">No data available</div>
            ) : (
              <>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
                      <Pie
                        data={bbsByCategory.filter(item => item.count > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={3}
                        dataKey="count"
                        label={(entry) => `${entry.name}: ${entry.count} ${entry.count === 1 ? 'User' : 'Users'}`}
                        labelLine={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1 }}
                      >
                        {bbsByCategory.filter(item => item.count > 0).map((item, index) => {
                          const originalIndex = bbsByCategory.findIndex(cat => cat.name === item.name);
                          return <Cell key={`cat-${index}`} fill={CATEGORY_COLORS[originalIndex % CATEGORY_COLORS.length]} />;
                        })}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 space-y-1.5">
                  {bbsByCategory.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-2 text-xs">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                      />
                      <span className="text-muted-foreground">{item.name}</span>
                      <span className="ml-auto font-semibold text-foreground">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend + Demographic */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">BBS Trend Over Time</CardTitle>
                <CardDescription>Monthly average & median score</CardDescription>
              </div>
              {bbsTrend.length >= 2 && (
                <span className="rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                  {bbsTrend[0].month} – {bbsTrend[bbsTrend.length - 1].month}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">Loading...</div>
            ) : bbsTrend.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">No data available</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={bbsTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line type="monotone" dataKey="avg" name="Average" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))" }} connectNulls />
                    <Line type="monotone" dataKey="median" name="Median" stroke="hsl(var(--chart-3))" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "hsl(var(--chart-3))" }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">BBS by Demographic</CardTitle>
            <CardDescription>Average score by gender & age group</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">Loading...</div>
            ) : bbsByDemographic.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">No data available</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bbsByDemographic} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} domain={[0, 100]} />
                    <YAxis type="category" dataKey="group" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={70} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value}`, "Avg BBS"]} />
                    <Bar dataKey="avg" name="Avg BBS" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBiteBalance;
